import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Crown, Check, Brain, Bookmark, Bell, Sparkles, CreditCard } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const features = [
  { icon: Brain, text: 'Unlimited AI "In Toto" Analyzer access' },
  { icon: Sparkles, text: 'Ad-free experience' },
  { icon: Bell, text: 'Priority story submissions' },
  { icon: Bookmark, text: 'Unlimited bookmarks' },
  { icon: CreditCard, text: 'Exclusive monthly reports' },
];

export default function SubscriptionPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    if (!user) {
      toast.error('Please sign in first');
      navigate('/auth');
      return;
    }

    if (user.is_premium) {
      toast.info('You are already a premium member!');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const originUrl = window.location.origin;
      
      const res = await axios.post(
        `${API}/subscription/checkout`,
        { origin_url: originUrl },
        { 
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true 
        }
      );
      
      // Redirect to Stripe
      window.location.href = res.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating checkout session');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-12 px-4 md:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <Crown className="w-16 h-16 mx-auto mb-6 text-yellow-500" />
          <h1 className="font-heading text-4xl md:text-6xl font-bold mb-4" data-testid="subscription-title">
            Premium Access
          </h1>
          <p className="text-white/50 max-w-xl mx-auto">
            Unlock the complete interconnected picture with unlimited AI analysis and exclusive features.
          </p>
        </div>

        {/* Pricing Card */}
        <div className="max-w-md mx-auto">
          <div className="card-dark overflow-hidden" data-testid="pricing-card">
            {/* Price header */}
            <div className="bg-gradient-to-r from-yellow-500/20 to-amber-500/20 p-8 text-center border-b border-white/5">
              <p className="font-mono text-sm uppercase tracking-wider text-yellow-500/80 mb-2">Monthly</p>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-5xl font-bold text-white">$9.99</span>
                <span className="text-white/40">/month</span>
              </div>
            </div>

            {/* Features */}
            <div className="p-8">
              <ul className="space-y-4 mb-8">
                {features.map((feature, idx) => {
                  const Icon = feature.icon;
                  return (
                    <li key={idx} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-yellow-500/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-yellow-500" />
                      </div>
                      <span className="text-white/70">{feature.text}</span>
                    </li>
                  );
                })}
              </ul>

              {user?.is_premium ? (
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-sm mb-4">
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="text-green-500 font-mono text-sm">Active Premium Member</span>
                  </div>
                  <p className="text-white/40 text-sm">You have full access to all premium features.</p>
                </div>
              ) : (
                <Button 
                  onClick={handleSubscribe} 
                  className="w-full btn-primary bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-black"
                  disabled={loading}
                  data-testid="subscribe-btn"
                >
                  {loading ? 'Processing...' : 'Subscribe Now'}
                </Button>
              )}
            </div>
          </div>

          {/* Disclaimer */}
          <p className="text-white/30 text-center text-sm mt-6">
            Cancel anytime. Secure payment via Stripe.
          </p>
        </div>
      </div>
    </div>
  );
}
